import React from 'react';

import ActivityIndicator from '~/UI/ActivityIndicator';
import ERPBaseScenario from './ERPBaseScenario';
import ERPDetailSummary from './ERPDetailSummary';
import ERPInterventions from './ERPInterventions';
import ERPInterventionScenarioModalContainer from './ERPInterventionModal/ERPInterventionScenarioModalContainer';
import ERPPriceVolumeGrowthModalContainer from './ERPPriceVolumeGrowthModal/ERPPriceVolumeGrowthModalContainer';
import ERPHeader from './ERPHeader';
import NotFoundErrorPage from '~/UI/NotFoundErrorPage/NotFoundErrorPage';
import TailwindLayout from './TailwindLayout';

import { useSelector } from 'react-redux';
import { ApplicationState } from '~/Store';

import {
	ERPSessionError,
	ERPSessionRequest,
	ERPSessionResponse,
	getHumanReadableErrorCode,
} from './api/ERPSessionRequest';

import { useApiResult } from '~/infrastructure/mam/ApiHooks';

import ERPScenarioComparison, { pickScenario } from './ERPScenarioComparison';
import ServerErrorPage from '~/UI/ServerErrorPage/ServerErrorPage';
import { Initial, Failure } from '~/infrastructure/mam/Result';

import config from '~/config';
import { useCacheSuccessfulApiResult } from '~/infrastructure/mam/ApiHooks/useApiResult';
import { createErpSessionLink } from './utils/url';
import { SortBy } from './domain';
import { SortingContextProvider } from './store/sortingContext';
import { ErpScenarioResponse } from 'com.cogvio.pricemonitor.api';

interface ERPContainerProps {
	defaultCurrency: string | null;
	erpSessionId: string;
	erpPackId: string;
}

const ERPContainer = ({ defaultCurrency, erpSessionId, erpPackId }: ERPContainerProps) => {
	const [dataModalVisible, setDataModalVisible] = React.useState<boolean>(false);
	const [newScenarioModalVisible, setNewScenarioModalVisible] = React.useState<boolean>(false);
	const [scenarioIdToEdit, setScenarioIdToEdit] = React.useState<null | string>(null);
	const sortBy = React.useRef(SortBy.PRICE);

	const toggleDataModalVisibility = React.useCallback(() => {
		setDataModalVisible((prev) => !prev);
	}, [setDataModalVisible]);

	const toggleNewScenarioModalVisibility = React.useCallback(() => {
		setNewScenarioModalVisible(true);
	}, [setNewScenarioModalVisible]);

	const hideInterventionScenarioModal = React.useCallback(() => {
		setScenarioIdToEdit(null);
		setNewScenarioModalVisible(false);
	}, [setNewScenarioModalVisible, setScenarioIdToEdit]);

	const userId = useSelector(({ accessToken }: ApplicationState) => accessToken.accessToken?.user.id);

	const [currentSessionResult, reloadERPSession] = useApiResult<ERPSessionRequest, ERPSessionResponse, ERPSessionError>(
		userId ? new ERPSessionRequest(userId, erpPackId, erpSessionId) : null,
		[userId, erpSessionId, erpPackId]
	);

	const erpSessionResult = useCacheSuccessfulApiResult(currentSessionResult);

	const erpSessionDate = React.useMemo(
		() =>
			erpSessionResult.value?.erpSessionDetail.erpSession.created
				? new Date(erpSessionResult.value.erpSessionDetail.erpSession.created)
				: new Date(),
		[erpSessionResult.value?.erpSessionDetail.erpSession.created]
	);

	const currency =
		defaultCurrency ||
		erpSessionResult.value?.erpSessionDetail.inputPrices[0]?.price.price.currency ||
		config.analytics.defaultCurrency;

	if (currentSessionResult instanceof Failure) {
		return (
			<ServerErrorPage
				error={currentSessionResult.error}
				title={getHumanReadableErrorCode(
					currentSessionResult.error.codes,
					erpSessionId,
					erpPackId,
					createErpSessionLink(erpSessionId, erpPackId)
				)}
			/>
		);
	}

	if (erpSessionResult instanceof Initial || !userId) {
		return <ActivityIndicator />;
	}

	const erpSessionDetail = erpSessionResult.value?.erpSessionDetail || null;

	return (
		<SortingContextProvider>
			<ERPHeader />
			<TailwindLayout>
				{erpSessionResult.value ? (
					<>
						<ERPDetailSummary
							erpDate={erpSessionDate}
							virtualProductPackName={erpSessionResult.value?.erpSessionDetail.erpPack.virtualProductPackName || ''}
							onDataAddRequest={toggleDataModalVisibility}
						/>
						<ERPBaseScenario
							currency={currency}
							erpSessionResult={erpSessionResult}
							virtualProductPackName={erpSessionResult.value?.erpSessionDetail.erpPack.virtualProductPackName || ''}
							onDataAddRequest={toggleDataModalVisibility}
						/>
						<ERPInterventions
							currency={currency}
							erpSessionResult={erpSessionResult}
							userId={userId}
							virtualProductPackName={erpSessionResult.value?.erpSessionDetail.erpPack.virtualProductPackName || ''}
							onNewScenarioCreate={toggleNewScenarioModalVisibility}
							onScenarioEdit={setScenarioIdToEdit}
						/>
						<ERPScenarioComparison
							currency={currency}
							erpSessionResult={erpSessionResult}
							userId={userId}
							onDataAddRequest={toggleDataModalVisibility}
						/>
						<ERPPriceVolumeGrowthModalContainer
							currency={currency}
							erpDate={erpSessionDate}
							erpSessionResult={erpSessionResult}
							modalVisible={dataModalVisible}
							userId={userId}
							virtualProductPackName={erpSessionResult.value?.erpSessionDetail.erpPack.virtualProductPackName || ''}
							onClose={toggleDataModalVisibility}
							onERPSessionReload={reloadERPSession}
						/>
						{erpSessionDetail && (
							<ERPInterventionScenarioModalContainer
								currency={currency}
								erpDate={erpSessionDate}
								erpPackId={erpSessionDetail.erpPack.id}
								erpSessionId={erpSessionDetail.erpSession.id}
								erpSessionResult={erpSessionResult}
								modalVisible={Boolean(scenarioIdToEdit || newScenarioModalVisible)}
								scenario={
									(pickScenario(scenarioIdToEdit, erpSessionDetail.erpSession.scenarios) as ErpScenarioResponse) ||
									undefined
								}
								userId={userId}
								onClose={hideInterventionScenarioModal}
								onSubmit={reloadERPSession}
							/>
						)}
					</>
				) : (
					<NotFoundErrorPage />
				)}
			</TailwindLayout>
		</SortingContextProvider>
	);
};

export default React.memo(ERPContainer);
